import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "../../../supabase/supabase";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Loader2,
  Save,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Upload,
  X,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Transcript } from "@/types/prompt";
import InlineDiff from "./InlineDiff";

type Message = {
  role: "user" | "assistant" | "system";
  content: string;
};

export default function PromptGenerator() {
  const [searchParams] = useSearchParams();
  const transcriptId = searchParams.get("transcriptId");

  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [generatedPrompt, setGeneratedPrompt] = useState("");
  const [previousPrompt, setPreviousPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("transcript");
  const [feedback, setFeedback] = useState("");
  const [chatHistory, setChatHistory] = useState<Message[]>([]);
  const [manualTranscript, setManualTranscript] = useState("");
  const [showDiff, setShowDiff] = useState(false);
  const [pendingChanges, setPendingChanges] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (transcriptId) {
      fetchTranscript(transcriptId);
    }
  }, [transcriptId]);

  const fetchTranscript = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from("transcripts")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      setTranscript(data);
    } catch (error) {
      console.error("Error fetching transcript:", error);
      toast({
        title: "Error",
        description: "Failed to load transcript. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTranscriptInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const content = e.target.value;
    setManualTranscript(content);

    if (content.trim()) {
      setTranscript({
        id: "temp-id",
        name: "Manual transcript",
        user_id: "",
        organization_id: "",
        content: content,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } else {
      setTranscript(null);
    }
  };

  const generatePrompt = async () => {
    if (!transcript) {
      toast({
        title: "Error",
        description: "No transcript provided. Please add a transcript first.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGenerating(true);
      setActiveTab("generated");

      // Get the session token for authorization
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Claude Supabase function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate_prompt_claude`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            transcript: transcript.content,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to generate prompt");
      }

      const data = await response.json();
      const generatedContent = data.prompt;

      setGeneratedPrompt(generatedContent);

      // Add to chat history
      setChatHistory([
        {
          role: "system",
          content:
            "I've analyzed the transcript and generated an initial prompt following VAPI guidelines.",
        },
        { role: "assistant", content: generatedContent },
      ]);

      setIsGenerating(false);
    } catch (error) {
      console.error("Error generating prompt:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to generate prompt. Please try again.",
        variant: "destructive",
      });
      setIsGenerating(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) return;

    try {
      // Add user feedback to chat history
      const updatedHistory = [
        ...chatHistory,
        { role: "user" as const, content: feedback },
      ];
      setChatHistory(updatedHistory);
      setFeedback("");
      setIsGenerating(true);

      // Save the current prompt for comparison
      setPreviousPrompt(generatedPrompt);

      // Get the session token for authorization
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      // Call the Claude Supabase function with the conversation history
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate_prompt_claude`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            conversationHistory: updatedHistory.map((msg) => ({
              role: msg.role === "system" ? "assistant" : msg.role,
              content: msg.content,
            })),
            instructions:
              "Refine the prompt based on the user's feedback. Return the complete updated prompt.",
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to refine prompt");
      }

      const data = await response.json();
      const refinedPrompt = data.prompt;

      // Show diff view for user to accept/reject
      setGeneratedPrompt(refinedPrompt);
      setShowDiff(true);
      setPendingChanges(true);

      // Add AI response to chat history
      setChatHistory([
        ...updatedHistory,
        { role: "assistant", content: refinedPrompt },
      ]);
      setIsGenerating(false);
    } catch (error) {
      console.error("Error refining prompt:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to refine prompt. Please try again.",
        variant: "destructive",
      });
      setIsGenerating(false);
    }
  };

  const acceptChanges = () => {
    setShowDiff(false);
    setPendingChanges(false);
    toast({
      title: "Changes Accepted",
      description: "The prompt has been updated with your changes.",
    });
  };

  const rejectChanges = () => {
    // Revert to previous prompt
    setGeneratedPrompt(previousPrompt);
    setShowDiff(false);
    setPendingChanges(false);

    // Update chat history to indicate rejection
    setChatHistory([
      ...chatHistory.slice(0, -1),
      {
        role: "system",
        content: "Changes were rejected. Reverted to previous version.",
      },
    ]);

    toast({
      title: "Changes Rejected",
      description: "Reverted to the previous version of the prompt.",
    });
  };

  const savePrompt = async () => {
    if (!title.trim()) {
      toast({
        title: "Error",
        description: "Please provide a title for your prompt.",
        variant: "destructive",
      });
      return;
    }

    if (!generatedPrompt.trim()) {
      toast({
        title: "Error",
        description: "No prompt has been generated yet.",
        variant: "destructive",
      });
      return;
    }

    if (pendingChanges) {
      toast({
        title: "Pending Changes",
        description:
          "Please accept or reject the current changes before saving.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);

      // Get the current user
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Get the user's organization
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("organization_id")
        .eq("id", user.id)
        .single();

      if (userError) throw userError;
      if (!userData.organization_id)
        throw new Error("User has no organization");

      // If we have a manual transcript, save it first
      let transcriptIdToUse = transcript?.id;

      if (
        transcript &&
        transcript.id === "temp-id" &&
        manualTranscript.trim()
      ) {
        const { data: newTranscript, error: transcriptError } = await supabase
          .from("transcripts")
          .insert({
            user_id: user.id,
            organization_id: userData.organization_id,
            content: manualTranscript.trim(),
          })
          .select()
          .single();

        if (transcriptError) throw transcriptError;
        transcriptIdToUse = newTranscript.id;
      }

      // Save the prompt
      const { data: promptData, error: promptError } = await supabase
        .from("prompts")
        .insert({
          user_id: user.id,
          organization_id: userData.organization_id,
          transcript_id: transcriptIdToUse || null,
          title,
          description,
        })
        .select()
        .single();

      if (promptError) throw promptError;

      // Save the initial version
      const { error: versionError } = await supabase
        .from("prompt_versions")
        .insert({
          prompt_id: promptData.id,
          content: generatedPrompt,
          version_number: 1,
          created_by: user.id,
        });

      if (versionError) throw versionError;

      toast({
        title: "Success",
        description: "Prompt saved successfully.",
      });

      // Navigate to the prompt detail page
      navigate(`/prompt-engine/prompt/${promptData.id}`);
    } catch (error) {
      console.error("Error saving prompt:", error);
      toast({
        title: "Error",
        description: "Failed to save prompt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center mb-2">
          <Button
            variant="ghost"
            size="sm"
            className="mr-2 -ml-2"
            onClick={() => navigate("/prompt-engine")}
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
        <CardTitle className="text-xl">
          Generate AI Prompt with Claude
        </CardTitle>
        <CardDescription>
          Generate an AI prompt based on the transcript and refine it through
          conversation with Claude 3.7 Sonnet.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="title">Prompt Title</Label>
            <Input
              id="title"
              placeholder="Enter a title for your prompt"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Input
              id="description"
              placeholder="Enter a brief description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="transcript">Transcript</TabsTrigger>
            <TabsTrigger value="generated">Generated Prompt</TabsTrigger>
          </TabsList>
          <TabsContent
            value="transcript"
            className="border rounded-md p-4 min-h-[300px] max-h-[500px] overflow-auto"
          >
            {transcript && transcript.id !== "temp-id" ? (
              <pre className="whitespace-pre-wrap font-mono text-sm">
                {transcript.content}
              </pre>
            ) : (
              <div className="flex flex-col space-y-4">
                <p className="text-muted-foreground mb-2">
                  {transcript
                    ? "Edit your transcript below:"
                    : "No transcript provided. Paste your transcript below:"}
                </p>
                <Textarea
                  placeholder="Paste your conversation transcript here..."
                  className="min-h-[200px] font-mono text-sm w-full"
                  value={manualTranscript}
                  onChange={handleTranscriptInput}
                />
                <div className="text-center text-sm text-muted-foreground">
                  <p>Or you can go back to create a transcript first</p>
                  <Button
                    variant="link"
                    onClick={() => navigate("/prompt-engine/transcript/create")}
                    className="mt-2"
                  >
                    <Upload className="h-4 w-4 mr-1" /> Create New Transcript
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
          <TabsContent value="generated" className="space-y-4">
            {isGenerating ? (
              <div className="flex flex-col items-center justify-center h-[300px]">
                <Loader2 className="h-8 w-8 animate-spin mb-4" />
                <p>
                  {previousPrompt
                    ? "Refining prompt based on feedback..."
                    : "Generating prompt with Claude..."}
                </p>
              </div>
            ) : generatedPrompt ? (
              <div className="space-y-4">
                {showDiff ? (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-sm font-medium">Review Changes</h3>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-good"
                          onClick={acceptChanges}
                        >
                          <Check className="h-4 w-4 mr-1" /> Accept
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-danger"
                          onClick={rejectChanges}
                        >
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    </div>
                    <div className="border rounded-md overflow-hidden">
                      <InlineDiff
                        oldText={previousPrompt}
                        newText={generatedPrompt}
                        onOldTextChange={(text) => setPreviousPrompt(text)}
                        onNewTextChange={(text) => setGeneratedPrompt(text)}
                      />
                    </div>
                  </div>
                ) : (
                  <ScrollArea className="border rounded-md p-4 h-[300px]">
                    <pre className="whitespace-pre-wrap font-mono text-sm">
                      {generatedPrompt}
                    </pre>
                  </ScrollArea>
                )}

                <div className="space-y-2">
                  <Label htmlFor="feedback">
                    Provide Feedback to Refine the Prompt
                  </Label>
                  <div className="flex space-x-2">
                    <Textarea
                      id="feedback"
                      placeholder="Suggest improvements or changes to the prompt..."
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      className="flex-1"
                      disabled={isGenerating || pendingChanges}
                    />
                    <Button
                      onClick={handleFeedbackSubmit}
                      disabled={!feedback.trim() || pendingChanges}
                      isLoading={isGenerating}
                      loadingText="Sending..."
                    >
                      Send
                    </Button>
                  </div>
                </div>

                {chatHistory.length > 0 && (
                  <div className="border rounded-md p-4 max-h-[300px] overflow-auto">
                    <h3 className="text-sm font-medium mb-2">
                      Conversation History
                    </h3>
                    <div className="space-y-4">
                      {chatHistory.map((message, index) => (
                        <div
                          key={index}
                          className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg p-3 ${message.role === "user" ? "bg-primary text-primary-foreground" : message.role === "system" ? "bg-secondary text-secondary-foreground" : "bg-muted"}`}
                          >
                            <p className="whitespace-pre-wrap text-sm">
                              {message.content.length > 500
                                ? `${message.content.substring(0, 500)}... (full content in prompt above)`
                                : message.content}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[300px] space-y-4">
                <p className="text-muted-foreground">
                  No prompt has been generated yet.
                </p>
                <Button
                  onClick={generatePrompt}
                  disabled={!transcript}
                  isLoading={isGenerating}
                  loadingText="Generating..."
                >
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Prompt with Claude
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate("/prompt-engine")}
          disabled={isGenerating || isSaving}
        >
          Cancel
        </Button>
        <div className="space-x-2">
          {!generatedPrompt && (
            <Button
              onClick={generatePrompt}
              disabled={!transcript}
              isLoading={isGenerating}
              loadingText="Generating..."
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Generate Prompt
            </Button>
          )}
          {generatedPrompt && !pendingChanges && (
            <Button
              onClick={savePrompt}
              disabled={!title.trim()}
              isLoading={isSaving}
              loadingText="Saving..."
            >
              <Save className="mr-2 h-4 w-4" />
              Save Prompt
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}
